/**
 * `Section` (W1-12 ui-kit-i18n; contract SectionProps): an inspector / settings section with a heading. When
 * collapsible, the heading holds a disclosure button (aria-expanded, aria-controls; Enter / Space toggle) and the
 * body is hidden while collapsed. Header actions (icon buttons) sit on the right and never toggle the section.
 */

import { useId, useState } from 'react';
import { Icon } from './Icon';
import { cx } from './internal/cx';
import s from './Section.module.css';
import type { SectionProps } from './types';

export interface SectionExtraProps {
  readonly className?: string | undefined;
  /** Controlled open state (otherwise `defaultOpen`, default true). */
  readonly open?: boolean | undefined;
  onOpenChange?(open: boolean): void;
  /** Heading level for the document outline (default 3). */
  readonly level?: 2 | 3 | 4 | undefined;
}

export function Section(props: SectionProps & SectionExtraProps) {
  const headingId = useId();
  const bodyId = useId();
  const [ownOpen, setOwnOpen] = useState(props.defaultOpen ?? true);
  const collapsible = props.collapsible === true;
  const open = !collapsible || (props.open ?? ownOpen);
  const Heading = props.level === 2 ? 'h2' : props.level === 4 ? 'h4' : 'h3';
  const toggle = (): void => {
    const next = !open;
    if (props.open === undefined) setOwnOpen(next);
    props.onOpenChange?.(next);
  };
  return (
    <section className={cx(s.section, props.className)} aria-labelledby={headingId} data-open={open}>
      <div className={s.header}>
        <Heading id={headingId} className={s.heading}>
          {collapsible ? (
            <button type="button" className={s.toggle} aria-expanded={open} aria-controls={bodyId} onClick={toggle}>
              <Icon name="ChevronRight" size={14} />
              <span className={s.title}>{props.title}</span>
            </button>
          ) : (
            <span className={s.title}>{props.title}</span>
          )}
        </Heading>
        {props.actions !== undefined && props.actions !== null && <div className={s.actions}>{props.actions}</div>}
      </div>
      <div id={bodyId} className={s.body} hidden={!open}>
        {props.children}
      </div>
    </section>
  );
}
